import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Loader2, Mail, MapPin, PhoneCall, Send, Siren } from "lucide-react";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { PageHead } from "@/components/PageHead";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENTS = ["Free IT Review", "General enquiry", "Project quote", "Existing client support"];
const TEAM_SIZES = ["1–10", "11–25", "26–50", "51–100", "100+"];

const inputCls =
  "w-full rounded-xl border border-input bg-card px-4 py-3 text-sm outline-none transition-all duration-200 focus:border-primary focus:ring-2 focus:ring-primary/20 placeholder:text-muted-foreground/60";

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium">
      {label}
    </label>
    {children}
  </div>
);

export default function Contact() {
  const [params] = useSearchParams();
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    users: TEAM_SIZES[0],
    intent: params.get("intent") === "review" ? "Free IT Review" : "General enquiry",
    message: "",
  });

  useEffect(() => {
    if (params.get("intent") === "review") {
      setForm((f) => ({ ...f, intent: "Free IT Review" }));
    }
  }, [params]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Your enquiry has been sent successfully. We've emailed you a confirmation, and a real person will reply within one business day.");
      setForm({ ...form, name: "", company: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong sending your enquiry. Please call or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHead
        eyebrow="Contact"
        title={
          form.intent === "Free IT Review"
            ? "Request your free IT review."
            : "Get in touch."
        }
        sub="No sales scripts, no obligation. A clear 30-minute look at your setup, or a straightforward answer to your question."
        testid="contact-page-title"
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-28">
        <div className="grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="space-y-4">
              <a
                href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                data-testid="contact-phone"
                className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40"
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-forest text-blue-300">
                  <PhoneCall className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">Phone</span>
                  <span className="block font-semibold">{CONTACT.phone}</span>
                </span>
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email"
                className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40"
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-forest text-blue-300">
                  <Mail className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">Email</span>
                  <span className="block font-semibold break-all">{CONTACT.email}</span>
                </span>
              </a>
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#25D366]/50"
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#25D366] text-white">
                  <WhatsAppIcon className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">WhatsApp</span>
                  <span className="block font-semibold">Message us directly and we'll reply promptly</span>
                </span>
              </a>
              <div className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5" data-testid="contact-service-area">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-forest text-blue-300">
                  <MapPin className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">Service area</span>
                  <span className="block text-sm font-medium leading-relaxed">{CONTACT.area}</span>
                </span>
              </div>
              <div className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5" data-testid="contact-hours">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-forest text-blue-300">
                  <Clock className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">Office hours</span>
                  <span className="block text-sm font-medium leading-relaxed">{CONTACT.hours}</span>
                </span>
              </div>
              <div className="flex items-start gap-4 rounded-2xl border border-primary/30 bg-accent p-5" data-testid="urgent-support-note">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground">
                  <Siren className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">Something down right now?</span>
                  <span className="block text-sm font-medium leading-relaxed">
                    Existing clients with urgent issues should call {CONTACT.phone} directly. Average triage is under 15 minutes.
                  </span>
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="rounded-3xl border border-border bg-card p-7 sm:p-9 space-y-5"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full name" htmlFor="name">
                  <input id="name" data-testid="form-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Doe" />
                </Field>
                <Field label="Company" htmlFor="company">
                  <input id="company" data-testid="form-company-input" value={form.company} onChange={set("company")} className={inputCls} placeholder="Doe & Co Ltd" />
                </Field>
                <Field label="Business email" htmlFor="email">
                  <input id="email" type="email" data-testid="form-email-input" required value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@company.co.uk" />
                </Field>
                <Field label="Phone" htmlFor="phone">
                  <input id="phone" type="tel" data-testid="form-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="07…" />
                </Field>
                <Field label="Team size" htmlFor="users">
                  <select id="users" data-testid="form-users-select" value={form.users} onChange={set("users")} className={inputCls}>
                    {TEAM_SIZES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="I'm contacting about" htmlFor="intent">
                  <select id="intent" data-testid="form-intent-select" value={form.intent} onChange={set("intent")} className={inputCls}>
                    {INTENTS.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Your message" htmlFor="message">
                <textarea
                  id="message"
                  data-testid="form-message-input"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  className={`${inputCls} resize-none`}
                  placeholder="Tell us a little about your current setup or the problem you're facing…"
                />
              </Field>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit-button"
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-primary px-8 py-3.5 font-semibold text-primary-foreground transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30 active:scale-[0.98] disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? "Sending…" : form.intent === "Free IT Review" ? "Request my free review" : "Send enquiry"}
              </button>
              <p className="text-xs text-muted-foreground">
                We'll only use your details to reply to this enquiry. No newsletters, no cold calls.
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
